import { FormDto } from 'app/models/generated'
import { isSoftDeleted } from 'app/api/client/softDelete'
import { AccForm } from '../dto/accFormDto'
import { logger } from 'app/logging/Logger'
import { ApiHelper } from 'app/api/api-helper'
import { ApiRequester } from 'app/api/api-requestor'
import {
    SubmitFormToFHIRResponse,
    DownloadFormPDFResponse,
    MarkFormEnteredInErrorResponse,
} from 'app/api/types'
import {
    fetchVersion,
    executeMutation,
    executeQuery,
    subscribeGraphQl,
} from 'app/api/client/graphqlClient'
import {
    getFormDtoQuery,
    createFormDtoMutation,
    updateFormDtoMutation,
    deleteFormDtoMutation,
    listQuestionnaireDtosQuery,
    formDtosByConsultIdQuery,
    onCreateFormDtoSubscription,
    onUpdateFormDtoSubscription,
} from 'app/graphql/customQueries'

/**
 * Subscribe to a single form by ID.
 * Uses a direct GraphQL query for initial load + onUpdateFormDto subscription for real-time updates.
 *
 * @param id - Form ID
 * @param callback - Callback function to receive form updates
 * @returns Subscription object with unsubscribe method. Caller must unsubscribe on cleanup.
 */
export const subscribeToForm = (
    id: string,
    callback: (accForm: FormDto) => void
): { unsubscribe: () => void } | undefined => {
    try {
        // Initial fetch via direct GraphQL query
        executeQuery<FormDto | null>(getFormDtoQuery, { id }, 'getFormDto')
            .then((form) => {
                if (form && !isSoftDeleted(form)) {
                    callback(form)
                }
            })
            .catch((error) => {
                logger.error(
                    'Error fetching form by ID',
                    error instanceof Error ? error : new Error(String(error)),
                    {
                        action: 'fetch_form_by_id_error',
                        metadata: { formId: id },
                    }
                )
            })

        // Subscribe to updates for this form
        const sub = subscribeGraphQl(
            onUpdateFormDtoSubscription,
            { filter: { id: { eq: id } } },
            {
                next: ({ data }) => {
                    const form = data?.onUpdateFormDto as FormDto | undefined
                    if (form) {
                        callback(form)
                    }
                },
                error: (error: unknown) => {
                    logger.error(
                        'Form subscription error',
                        error instanceof Error
                            ? error
                            : new Error(String(error)),
                        {
                            action: 'form_subscription_error',
                            metadata: { formId: id },
                        }
                    )
                },
            }
        )

        return { unsubscribe: () => sub.unsubscribe() }
    } catch (error) {
        const err =
            error instanceof Error
                ? error
                : new Error('Error subscribing to form')
        logger.error('Error subscribing to form', err, {
            action: 'subscribe_form_error',
            metadata: { formId: id, error: err.message },
        })
        return undefined
    }
}

/**
 * Subscribe to forms for a specific consult.
 * Uses a direct GraphQL GSI query for initial load + onCreate/onUpdate subscriptions for real-time updates.
 *
 * @param id - Consult ID
 * @param callback - Callback function to receive forms updates
 * @returns Subscription object with unsubscribe method. Caller must unsubscribe on cleanup.
 */
export const subscribeToFormWithConsultId = (
    id: string,
    callback: (forms: FormDto[]) => void,
    /**
     * ACA-196: `live: false` does the initial load only, with no create/update
     * subscriptions. AppSync allows 200 subscriptions per WebSocket connection
     * and each live consult uses 2, so the Forms page loads older consults
     * (outside the consult cache) this way.
     */
    options: { live?: boolean } = {}
): { unsubscribe: () => void } | undefined => {
    const live = options.live ?? true
    try {
        logger.debug('Creating form subscription for consult', {
            action: 'create_form_subscription',
            metadata: { consultId: id },
        })

        // Local cache of forms, keyed by form ID
        const formsMap = new Map<string, FormDto>()

        const emitForms = () => {
            const forms = Array.from(formsMap.values()).filter(
                (f) => !isSoftDeleted(f)
            )
            callback(forms)
        }

        // Initial fetch via GSI query
        executeQuery<{ items: FormDto[]; nextToken: string | null }>(
            formDtosByConsultIdQuery,
            { consultId: id },
            'formDtosByConsultIdAndSentDate'
        )
            .then((result) => {
                const items = (result?.items || []).filter(
                    (f) => !isSoftDeleted(f)
                )
                items.forEach((f) => formsMap.set(f.id, f))

                logger.debug('Form initial load complete', {
                    action: 'form_initial_load',
                    metadata: { consultId: id, formCount: items.length },
                })

                emitForms()
            })
            .catch((error) => {
                logger.error(
                    'Error fetching forms by consult',
                    error instanceof Error ? error : new Error(String(error)),
                    {
                        action: 'fetch_forms_by_consult_error',
                        metadata: { consultId: id },
                    }
                )
            })

        if (!live) {
            return { unsubscribe: () => {} }
        }

        // Subscribe to new forms for this consult
        const createSub = subscribeGraphQl(
            onCreateFormDtoSubscription,
            { filter: { consultId: { eq: id } } },
            {
                next: ({ data }) => {
                    const form = data?.onCreateFormDto as FormDto | undefined
                    if (form) {
                        formsMap.set(form.id, form)
                        emitForms()
                    }
                },
                error: (error: unknown) => {
                    logger.error(
                        'Form create subscription error',
                        error instanceof Error
                            ? error
                            : new Error(String(error)),
                        {
                            action: 'form_create_sub_error',
                            metadata: { consultId: id },
                        }
                    )
                },
            }
        )

        // Subscribe to form updates for this consult
        const updateSub = subscribeGraphQl(
            onUpdateFormDtoSubscription,
            { filter: { consultId: { eq: id } } },
            {
                next: ({ data }) => {
                    const form = data?.onUpdateFormDto as FormDto | undefined
                    if (form) {
                        if (isSoftDeleted(form)) {
                            formsMap.delete(form.id)
                        } else {
                            formsMap.set(form.id, form)
                        }
                        emitForms()
                    }
                },
                error: (error: unknown) => {
                    logger.error(
                        'Form update subscription error',
                        error instanceof Error
                            ? error
                            : new Error(String(error)),
                        {
                            action: 'form_update_sub_error',
                            metadata: { consultId: id },
                        }
                    )
                },
            }
        )

        return {
            unsubscribe: () => {
                createSub.unsubscribe()
                updateSub.unsubscribe()
            },
        }
    } catch (error) {
        const err =
            error instanceof Error
                ? error
                : new Error('Error subscribing to forms by consult')
        logger.error('Error subscribing to forms by consult', err, {
            action: 'subscribe_forms_by_consult_error',
            metadata: { consultId: id, error: err.message },
        })
        return undefined
    }
}

export const submitFormToFHIR = async (
    formId: string,
    userId: string
): Promise<boolean> => {
    const operation = ApiHelper.buildSubmitFormToFHIROperation(formId, userId)

    const response =
        await ApiRequester.callPostOperationRequest<SubmitFormToFHIRResponse>(
            operation
        )

    logger.debug('Submit Form to FHIR response received', {
        action: 'submit_form_to_fhir',
        metadata: { formId, userId, response },
    })

    // Backend returns a success message string
    // Check if response contains "successfully" to determine success
    const isSuccess =
        typeof response === 'string' &&
        response.toLowerCase().includes('successfully')

    // Update form status to Complete if submission was successful
    if (isSuccess) {
        try {
            const version = await fetchVersion('FormDto', formId)

            await executeMutation(
                updateFormDtoMutation,
                {
                    input: {
                        id: formId,
                        status: 'Complete',
                        _version: version,
                    },
                },
                'updateFormDto'
            )

            logger.debug('Form status updated to Complete', {
                action: 'update_form_status',
                metadata: { formId, status: 'Complete' },
            })
        } catch (error) {
            const err =
                error instanceof Error ? error : new Error('Unknown error')
            logger.error('Error updating form status', err, {
                action: 'update_form_status_error',
                metadata: { formId, error: err.message },
            })
            // Don't throw - submission was successful even if status update failed
        }
    }

    return isSuccess
}

/**
 * Update a form via GraphQL mutation.
 *
 * @param formId - Form ID
 * @param updatedForm - Updated form data
 * @returns Promise that resolves when the form is updated
 */
export const updateForm = async (
    formId: string,
    updatedForm: AccForm
): Promise<void> => {
    try {
        logger.debug('Updating form via GraphQL', {
            action: 'update_form_start',
            metadata: { formId },
        })

        // Fetch the current version for optimistic concurrency
        const version = await fetchVersion('FormDto', formId)

        // Convert AccForm sections back to FormDto format
        const updatedSections = updatedForm.sections.map((section) => ({
            title: section.title,
            rows: section.rows.map((row) => ({
                columns: row.columns.map((columnGroup) =>
                    columnGroup.map((field) => ({
                        label: field.label,
                        content: field.content,
                        linkId: field.linkId || '',
                        code: field.code || '',
                        system: field.system || '',
                        codings: field.codings
                            ? field.codings.map((coding) => ({
                                  system: coding.system || '',
                                  code: coding.code || '',
                                  display: coding.display || '',
                              }))
                            : undefined,
                    }))
                ),
            })),
        }))

        // Update the form via GraphQL mutation
        await executeMutation(
            updateFormDtoMutation,
            {
                input: {
                    id: formId,
                    sections: updatedSections,
                    _version: version,
                },
            },
            'updateFormDto'
        )

        logger.debug('Form updated successfully', {
            action: 'update_form_success',
            metadata: { formId },
        })
    } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error')
        logger.error('Error updating form', err, {
            action: 'update_form_error',
            metadata: { formId, error: err.message },
        })
        throw err
    }
}

/**
 * Create a copy of a form for "Entered in error" workflow.
 * Creates a new form with CREATED status, copying all data from the original.
 *
 * @param originalFormId - Original form ID
 * @returns Promise that resolves with the new form ID
 */
export const createFormCopy = async (
    originalFormId: string
): Promise<string> => {
    try {
        logger.debug('Creating form copy for entered in error', {
            action: 'create_form_copy_start',
            metadata: { originalFormId },
        })

        // Fetch the original form via GraphQL query
        const original = await executeQuery<FormDto | null>(
            getFormDtoQuery,
            { id: originalFormId },
            'getFormDto'
        )

        if (!original) {
            throw new Error(`Form with ID ${originalFormId} not found`)
        }

        // Create new form with copied data via GraphQL mutation
        const newForm = await executeMutation<{ id: string }>(
            createFormDtoMutation,
            {
                input: {
                    type: original.type,
                    consultId: original.consultId,
                    title: original.title,
                    status: 'Created',
                    patientId: original.patientId,
                    patientName: original.patientName,
                    sentDate: new Date().toISOString(),
                    sections: original.sections,
                    response: original.response,
                },
            },
            'createFormDto'
        )

        logger.debug('Form copy created successfully', {
            action: 'create_form_copy_success',
            metadata: { originalFormId, newFormId: newForm.id },
        })

        return newForm.id
    } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error')
        logger.error('Error creating form copy', err, {
            action: 'create_form_copy_error',
            metadata: { originalFormId, error: err.message },
        })
        throw err
    }
}

/**
 * Delete a form via GraphQL mutation.
 *
 * @param formId - Form ID to delete
 * @returns Promise that resolves when the form is deleted
 */
export const deleteForm = async (formId: string): Promise<void> => {
    try {
        logger.debug('Deleting form', {
            action: 'delete_form_start',
            metadata: { formId },
        })

        const version = await fetchVersion('FormDto', formId)

        await executeMutation(
            deleteFormDtoMutation,
            {
                input: {
                    id: formId,
                    _version: version,
                },
            },
            'deleteFormDto'
        )

        logger.debug('Form deleted successfully', {
            action: 'delete_form_success',
            metadata: { formId },
        })
    } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error')
        logger.error('Error deleting form', err, {
            action: 'delete_form_error',
            metadata: { formId, error: err.message },
        })
        throw err
    }
}

/**
 * Update form status via GraphQL mutation.
 *
 * @param formId - Form ID
 * @param status - New status
 * @returns Promise that resolves when the status is updated
 */
export const updateFormStatus = async (
    formId: string,
    status: string
): Promise<void> => {
    try {
        logger.debug('Updating form status via GraphQL', {
            action: 'update_form_status_start',
            metadata: { formId, status },
        })

        const version = await fetchVersion('FormDto', formId)

        await executeMutation(
            updateFormDtoMutation,
            {
                input: {
                    id: formId,
                    status,
                    _version: version,
                },
            },
            'updateFormDto'
        )

        logger.debug('Form status updated successfully', {
            action: 'update_form_status_success',
            metadata: { formId, status },
        })
    } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error')
        logger.error('Error updating form status', err, {
            action: 'update_form_status_error',
            metadata: { formId, status, error: err.message },
        })
        throw err
    }
}

/**
 * Download form as PDF by requesting a signed S3 URL from the backend.
 *
 * @param formId - Form ID
 * @param userId - User ID for authorization
 * @returns Promise that resolves with the signed S3 URL
 */
export const downloadFormPDF = async (
    formId: string,
    userId: string
): Promise<string> => {
    try {
        logger.debug('Requesting PDF download URL', {
            action: 'download_pdf_start',
            metadata: { formId, userId },
        })

        const operation = ApiHelper.buildDownloadFormPDFOperation(
            formId,
            userId
        )

        const response =
            await ApiRequester.callPostOperationRequest<DownloadFormPDFResponse>(
                operation
            )

        logger.debug('PDF download URL received', {
            action: 'download_pdf_url_received',
            metadata: {
                formId,
                userId,
                hasUrl: !!response.downloadUrl,
                expiresIn: response.expiresIn,
            },
        })

        if (!response.downloadUrl) {
            throw new Error('No download URL received from server')
        }

        return response.downloadUrl
    } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error')
        logger.error('Error downloading form PDF', err, {
            action: 'download_pdf_error',
            metadata: { formId, userId, error: err.message },
        })
        throw err
    }
}

/**
 * Mark a form as "Entered in error" in FHIR server via backend API.
 * This updates the form status in the FHIR server to indicate it was entered incorrectly.
 *
 * @param formId - Form ID to mark as entered in error
 * @param userId - User ID for authorization
 * @returns Promise that resolves when the operation is complete
 */
export const markFormEnteredInError = async (
    formId: string,
    userId: string
): Promise<void> => {
    try {
        logger.debug('Marking form as entered in error in FHIR', {
            action: 'mark_form_error_fhir_start',
            metadata: {
                formId,
                formIdType: typeof formId,
                formIdLength: formId?.length,
                userId,
                userIdType: typeof userId,
            },
        })

        const operation = ApiHelper.buildMarkFormEnteredInErrorOperation(
            formId,
            userId
        )

        const response =
            await ApiRequester.callPostOperationRequest<MarkFormEnteredInErrorResponse>(
                operation
            )

        logger.debug('Form marked as entered in error in FHIR', {
            action: 'mark_form_error_fhir_success',
            metadata: { formId, userId, response },
        })

        // Also update form status via GraphQL
        await updateFormStatus(formId, 'Entered in error')
    } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error')
        logger.error('Error marking form as entered in error in FHIR', err, {
            action: 'mark_form_error_fhir_error',
            metadata: { formId, userId, error: err.message },
        })
        throw err
    }
}

/**
 * Fetch questionnaire by name to get metadata (title, publisher, etc.)
 *
 * @param name - Questionnaire name (matches FormDto.type)
 * @returns Promise that resolves with the questionnaire or null if not found
 */
export const fetchQuestionnaireByName = async (
    name: string
): Promise<{ title: string; publisher: string } | null> => {
    try {
        logger.debug('Fetching questionnaire by name', {
            action: 'fetch_questionnaire_start',
            metadata: { name },
        })

        const result = await executeQuery<{
            items: Array<{ id: string; name: string; json: string }>
        }>(
            listQuestionnaireDtosQuery,
            { filter: { name: { eq: name } } },
            'listQuestionnaireDtos'
        )

        if (!result?.items || result.items.length === 0) {
            logger.warn('Questionnaire not found', {
                action: 'questionnaire_not_found',
                metadata: { name },
            })
            return null
        }

        const questionnaire = result.items[0]

        // Parse the JSON to extract title and publisher
        try {
            const questionnaireData = JSON.parse(questionnaire.json)

            const title = questionnaireData.title || name
            const publisher = questionnaireData.publisher || 'Unknown'

            logger.debug('Questionnaire fetched successfully', {
                action: 'fetch_questionnaire_success',
                metadata: { name, title, publisher },
            })

            return { title, publisher }
        } catch (parseError) {
            logger.error(
                'Failed to parse questionnaire JSON',
                parseError as Error,
                {
                    action: 'questionnaire_parse_error',
                    metadata: { name },
                }
            )
            return null
        }
    } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error')
        logger.error('Error fetching questionnaire', err, {
            action: 'fetch_questionnaire_error',
            metadata: { name, error: err.message },
        })
        return null
    }
}
